import type { Answers, SectionColor } from "./types";

/**
 * Maps Kurzform answers → Detailanalysen the back-office should run.
 * Each entry has `triggerKeys` — if ANY of them is answered "ja" the
 * detail analysis is recommended.
 */

export type DetailAnalysisCertainty = "always" | "conditional";

export type DetailAnalysisDef = {
  key: string;
  title: string;
  /** "1" Sachwerte, "2" Haftung, "3" Menschen, "4" Liquidität */
  bereich: 1 | 2 | 3 | 4;
  color: SectionColor;
  certainty: DetailAnalysisCertainty;
  /** ANY of these answer keys = "ja" triggers this analysis. */
  triggerKeys: string[];
  description: string;
};

export const DETAIL_ANALYSES: DetailAnalysisDef[] = [
  // ───────── Bereich 1 — Sachwerte (orange) ─────────
  {
    key: "sachwerte",
    title: "Detailanalyse Sachwerte",
    bereich: 1,
    color: "pillarOrange",
    certainty: "conditional",
    description:
      "Vertiefende Erfassung der vorhandenen Vermögenswerte (Maschinen, Waren, Gebäude, …) inkl. Werten und Verteilung.",
    triggerKeys: [
      "asset_kbe_ja",
      "asset_tbe_elektronik_ja",
      "asset_tbe_masch_stat_ja",
      "asset_tbe_masch_mob_ja",
      "asset_selbstfahrend_ja",
      "asset_handelsware_ja",
      "asset_rohhilfsstoffe_ja",
      "asset_halbfertig_ja",
      "asset_kundeneigentum_ja",
      "asset_baustellen_ja",
      "asset_wertsachen_ja",
      "asset_immobilien_ja",
      "asset_photovoltaik_ja",
      "asset_ladestationen_ja",
      "asset_grundstuecke_ja",
      "asset_sonstige_vermoegen_ja",
    ],
  },
  {
    key: "kfz",
    title: "Detailanalyse KFZ",
    bereich: 1,
    color: "pillarOrange",
    certainty: "always",
    description:
      "Erfassung jedes Fahrzeugs mit Halter, Nutzung, Wert, Versicherungswünschen.",
    triggerKeys: ["kfz_eigene_ja", "kfz_fremde_ja", "kfz_sonstige_ja"],
  },
  {
    key: "transport",
    title: "Detailanalyse Transport",
    bereich: 1,
    color: "pillarOrange",
    certainty: "conditional",
    description:
      "Versand-/Bezugswege, Frachtverantwortung und Werte je Transportmittel.",
    triggerKeys: [
      "versand_ja",
      "transport_dritte_ja",
      "lagerhalter_ja",
      "spedition_ja",
      "konsignation_ja",
    ],
  },
  {
    key: "betriebsstaetten_sicherheit",
    title: "Detailanalyse Sicherungen / Brandschutz",
    bereich: 1,
    color: "pillarOrange",
    certainty: "conditional",
    description:
      "Vor-Ort-Bewertung der Brand-, Einbruch- und Notfallsicherungen pro Standort.",
    triggerKeys: [
      "sich_einfriedung_ja",
      "sich_ema_ja",
      "sich_bma_ja",
      "sich_rwa_ja",
      "sich_sprinkler_ja",
      "sich_stromabschaltung_ja",
      "sich_video_ja",
      "sich_notstrom_ja",
      "sich_sonstige_ja",
    ],
  },

  // ───────── Bereich 2 — Haftung / Recht (blue) ─────────
  {
    key: "haftung_betrieb",
    title: "Detailanalyse Betriebs- / Produkthaftpflicht",
    bereich: 2,
    color: "blue",
    certainty: "conditional",
    description:
      "Vertiefung der Haftungsrisiken aus Tätigkeit, Produkten, Lohnfertigung und Obhut.",
    triggerKeys: [
      "schadenersatz_ja",
      "fremde_grundstuecke_ja",
      "lohnfertigung_ja",
      "obhut_ja",
      "weiterverarbeitung_ja",
      "produktzusagen_ja",
      "rueckruf_ja",
      "labeling_ja",
      "montage_eigen_ja",
      "montage_fremd_ja",
      "bauleistungen_ja",
    ],
  },
  {
    key: "umwelt",
    title: "Detailanalyse Umwelt",
    bereich: 2,
    color: "blue",
    certainty: "always",
    description:
      "WHG-/UmweltHG-Anlagen, Abwasser, Tätigkeiten an Umweltanlagen, Geothermie, eigener Boden.",
    triggerKeys: ["umwelt_grundrisiko_ja"],
  },
  {
    key: "beratung_vermoegen",
    title: "Detailanalyse Beratende Berufe / Vermögensschäden",
    bereich: 2,
    color: "blue",
    certainty: "conditional",
    description:
      "Vermögensschadenhaftpflicht für planende, beratende und gutachterliche Tätigkeiten.",
    triggerKeys: ["beratung_planung_ja"],
  },
  {
    key: "rechtsstreit",
    title: "Detailanalyse Rechtsschutz",
    bereich: 2,
    color: "blue",
    certainty: "conditional",
    description:
      "Bewertung von Rechtsstreitrisiken aus Urheber-/Patent-/Werkverträgen sowie historischen Auseinandersetzungen.",
    triggerKeys: ["rechtsstreitrisiken_ja", "rechtsstreit_historie_ja"],
  },
  {
    key: "sensitive_branchen",
    title: "Detailanalyse Sensitive Branchen",
    bereich: 2,
    color: "blue",
    certainty: "conditional",
    description:
      "Spezielle Bedingungen / Ausschlüsse für Auto-, Luft-, Schifffahrts- und Bahnindustrie.",
    triggerKeys: ["sensitive_branchen_ja"],
  },

  // ───────── Bereich 3 — Menschen im Unternehmen (green) ─────────
  {
    key: "menschen",
    title: "Detailanalyse Menschen im Unternehmen",
    bereich: 3,
    color: "green",
    certainty: "conditional",
    description:
      "Vertiefung Belegschaft, externe Kräfte, Auslandsentsendung und Mobilität.",
    triggerKeys: [
      "leiharbeiter_ja",
      "subunternehmer_ja",
      "freelancer_ja",
      "entsendung_ja",
      "pkw_eigene_ja",
      "dienstreisen_ja",
    ],
  },
  {
    key: "gf_versorgung",
    title: "Detailanalyse Gesellschafter-/Geschäftsführer-Versorgung",
    bereich: 3,
    color: "green",
    certainty: "always",
    description:
      "Bestehende und gewünschte Versorgungs- und Absicherungsbausteine für GF und Gesellschafter.",
    triggerKeys: ["gf_versorgung_ja"],
  },

  // ───────── Bereich 4 — Liquidität / Ertragsausfall (black) ─────────
  {
    key: "betriebsunterbrechung",
    title: "Detailanalyse Betriebsunterbrechung",
    bereich: 4,
    color: "black",
    certainty: "conditional",
    description:
      "BU- und Folgekostenanalyse — eigener Betrieb, Zulieferer/Abnehmer, Versorgung.",
    triggerKeys: ["bu_ja", "bu_zuli_abn_ja", "behoerd_seuche_ja"],
  },
  {
    key: "cyber",
    title: "Detailanalyse Cyber",
    bereich: 4,
    color: "black",
    certainty: "conditional",
    description:
      "Cyber-Risikoprofil, IT-Sicherheits-Niveau, Vorfallhistorie und Versicherungsbedarf.",
    triggerKeys: [
      "cyberangriff_ja",
      "datenschutz_ja",
      "verlust_lizenzen_ja",
    ],
  },
  {
    key: "forderungen",
    title: "Detailanalyse Forderungsausfall / Mahnwesen",
    bereich: 4,
    color: "black",
    certainty: "conditional",
    description:
      "Forderungsmanagement, Factoring, Insolvenzhistorie und Bürgschaftsexposure.",
    triggerKeys: [
      "zahlungsausfall_ja",
      "insolvenz_erfahrung_ja",
      "factoring_ja",
      "buergschaften_ja",
    ],
  },
];

export const isDetailAnalysisTriggered = (
  def: DetailAnalysisDef,
  answers: Answers
): boolean => def.triggerKeys.some((k) => answers[k] === "ja");

export const groupTriggeredDetailAnalysesByBereich = (
  answers: Answers
): Record<1 | 2 | 3 | 4, DetailAnalysisDef[]> => {
  const out: Record<1 | 2 | 3 | 4, DetailAnalysisDef[]> = { 1: [], 2: [], 3: [], 4: [] };
  for (const da of DETAIL_ANALYSES) {
    if (isDetailAnalysisTriggered(da, answers)) {
      out[da.bereich].push(da);
    }
  }
  return out;
};
